/**
 * Vorlage „Plattieren“.
 *
 * Ein Stück Fleisch liegt zwischen Folie auf dem Brett. Der Fleischklopfer
 * schlägt mit der glatten Seite mehrmals darauf; das Stück wird mit jedem
 * Schlag flacher und breiter.
 */
import { ease, lerp, segment } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import { path, roundedRectPath, smooth } from '../paths.ts';
import { compact, faded, group, line, rect, shape, styles } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { motionLines } from '../parts/effects.ts';
import { counter, doubleArrow, GROUND_Y, shadow } from '../parts/stage.ts';
import { place } from '../parts/utensils.ts';
import { defineTemplate } from '../template.ts';

/** Brett. */
const BOARD = { x: 22, width: 196, height: 8 } as const;
const BOARD_TOP = GROUND_Y - BOARD.height;
const CX = BOARD.x + BOARD.width / 2;
/** Fleisch vorher und nachher. */
const MEAT = { width: [74, 132], height: [16, 7] } as const;
/** Schläge: Zeitpunkt des Auftreffens und Stelle relativ zur Mitte. */
const HITS: readonly (readonly [number, number])[] = [
  [0.16, -18],
  [0.32, 22],
  [0.48, -34],
  [0.64, 38],
  [0.76, 0],
];
/** Höhe des Ausholens. */
const SWING = 46;

/** Zeitplan. */
const TIME = { reset: [0.86, 1] } as const;

/** Fleischklopfer: glatte Schlagfläche unten im Ursprung, Stiel nach rechts oben. */
function mallet(): SceneNode[] {
  return [
    group([shape(roundedRectPath(-4, -66, 8, 52, 4), styles.solid('tint'))], {
      transform: { rotate: 38, ox: 0, oy: -14 },
    }),
    shape(roundedRectPath(-17, -16, 34, 16, 3), styles.solid('paper')),
    line(-13, -11, 13, -11, styles.fine('shade')),
  ];
}

/** Fleisch zwischen zwei Lagen Folie. */
function meat(width: number, height: number): SceneNode[] {
  const top = BOARD_TOP - height;
  const outline = roundedRectPath(CX - width / 2, top, width, height, Math.min(6, height / 2));
  return [
    shape(outline, styles.solid('shade')),
    shape(
      smooth(
        [
          { x: CX - width * 0.32, y: top + height * 0.55 },
          { x: CX - width * 0.05, y: top + height * 0.35 },
          { x: CX + width * 0.28, y: top + height * 0.6 },
        ],
        false,
      ),
      { stroke: 'paper', strokeWidth: STROKE.detail },
    ),
    // Folie oben, etwas breiter als das Fleisch
    shape(
      path()
        .move(CX - width / 2 - 10, BOARD_TOP)
        .quad(CX - width / 2 - 6, top - 2, CX - width / 2 + 6, top - 2)
        .line(CX + width / 2 - 6, top - 2)
        .quad(CX + width / 2 + 6, top - 2, CX + width / 2 + 10, BOARD_TOP)
        .toString(),
      { stroke: 'deep', strokeWidth: STROKE.fine },
    ),
  ];
}

/** Vorlage „Plattieren“. */
export const prepPoundTemplate = defineTemplate({
  id: 'prep.pound',
  group: 'prep',
  title: 'Plattieren',
  description:
    'Das Fleisch liegt zwischen Frischhaltefolie und wird mit der glatten Seite des Fleischklopfers gleichmäßig dünn geklopft.',
  durationSeconds: 5,
  keywords: [
    'plattieren',
    'plattiert',
    'flach klopfen',
    'dünn klopfen',
    'flachklopfen',
    'klopfen',
    'fleischklopfer',
    'plattiereisen',
  ],
  stillTime: 0.47,
  parameters: [],
  scene(_values, t) {
    const reset = ease.inOutSine(segment(t, ...TIME.reset));
    // Nach jedem Schlag wird das Fleisch ein Stück flacher.
    const done = HITS.filter(([at]) => t >= at).length;
    const flat = (done / HITS.length) * (1 - reset);
    const width = lerp(MEAT.width[0], MEAT.width[1], flat);
    const height = lerp(MEAT.height[0], MEAT.height[1], flat);
    const top = BOARD_TOP - height;

    // Der Klopfer holt zwischen den Schlägen aus, wandert zur nächsten Stelle und fällt schnell herab.
    const index = HITS.findIndex(([at]) => t < at);
    const last = HITS[HITS.length - 1] as readonly [number, number];
    let x: number;
    let lift: number;
    let toolOpacity: number;
    if (index === -1) {
      x = CX + last[1];
      lift = SWING * ease.inOutSine(segment(t, last[0] + 0.02, 0.86));
      toolOpacity = 1 - segment(t, 0.8, 0.86);
    } else {
      const [hitAt, hitX] = HITS[index] as readonly [number, number];
      const [prevAt, prevX] = index > 0 ? (HITS[index - 1] as readonly [number, number]) : [0, hitX];
      const raise = ease.inOutSine(segment(t, prevAt + 0.02, hitAt - 0.05));
      const fall = ease.inCubic(segment(t, hitAt - 0.05, hitAt));
      x = CX + lerp(prevX, hitX, raise);
      lift = SWING * raise * (1 - fall);
      toolOpacity = ease.outCubic(segment(t, 0, 0.06));
    }
    const impact = HITS.some(([at]) => t >= at && t < at + 0.05);

    return compact([
      ...counter(),
      shadow(CX, BOARD.width * 0.48),
      rect(BOARD.x, BOARD_TOP, BOARD.width, BOARD.height, 3, styles.solid('tint')),
      line(BOARD.x + 6, BOARD_TOP + 4, BOARD.x + BOARD.width - 6, BOARD_TOP + 4, styles.fine('shade')),
      ...meat(width, height),
      impact ? motionLines(x - 26, top - 4, 180, 8, 2, 6) : null,
      impact ? motionLines(x + 26, top - 4, 0, 8, 2, 6) : null,
      faded(toolOpacity, [place(x, top - 2 - lift, -lift * 0.4, mallet())]),
    ]);
  },
  hints() {
    return [doubleArrow({ x: CX - 66, y: BOARD_TOP - 28 }, { x: CX + 66, y: BOARD_TOP - 28 })];
  },
});
